import { Box, Typography } from "@mui/material";
import { getSoftConditionalGaps } from "./Gate3ReadinessChecklist";

const FONT = "Lato, sans-serif";

const GAP_TEXT = {
  fontFamily: FONT,
  fontWeight: 400,
  fontSize: { xs: 12, sm: 13, lg: 14 },
  lineHeight: { xs: "18px", sm: "18px", lg: "20px" },
  letterSpacing: 0,
  color: "#8A5C00",
};

const STATUS = {
  passed: { label: "Passed", color: "#00754E", bgcolor: "#E8FCF3", border: "1px solid #00754E" },
  gap: { label: "Gap found", color: "#555555", bgcolor: "#F5F5F5", border: "1px solid #BBBBBB" },
};

function StatusPill({ status }) {
  const tone = STATUS[status];
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        boxSizing: "border-box",
        height: { xs: 20, sm: 22, lg: 24 },
        px: { xs: "8px", sm: "8px", lg: "10px" },
        borderRadius: { xs: "10px", lg: "12px" },
        border: tone.border,
        bgcolor: tone.bgcolor,
        color: tone.color,
        fontFamily: FONT,
        fontWeight: 600,
        fontSize: { xs: 10, sm: 11, lg: 12 },
        lineHeight: { xs: "20px", sm: "22px", lg: "24px" },
        letterSpacing: 0,
        whiteSpace: "nowrap",
        flexShrink: 0,
      }}
    >
      {tone.label}
    </Box>
  );
}

export default function Gate3FlagEvaluation({ values = {}, customDays = "0" }) {
  const softGaps = getSoftConditionalGaps(values, customDays);
  const gapCount = softGaps.length;

  const rows = [
    { label: "Hard blockers", value: "None detected", status: "passed" },
    {
      label: "Conditional gaps",
      value: gapCount === 0 ? "None detected" : gapCount === 1 ? "1 detected" : `${gapCount} detected`,
      status: gapCount === 0 ? "passed" : "gap",
    },
    { label: "Checklist completeness", value: "Requirements met for conditional go", status: "passed" },
  ];

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: { xs: "100%", lg: 1012 },
        border: "1px solid #E6E6E6",
        borderRadius: { xs: "8px", lg: "12px" },
        bgcolor: "#FFFFFF",
        overflow: "hidden",
      }}
    >
      {rows.map((row, index) => (
        <Box
          key={row.label}
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr auto", sm: "minmax(140px, 200px) minmax(0, 1fr) auto", lg: "240px minmax(0, 1fr) auto" },
            columnGap: { xs: 1, sm: 2, lg: 3 },
            rowGap: { xs: 0.5, sm: 0 },
            alignItems: "center",
            px: { xs: 1.5, sm: 2, lg: 2.5 },
            py: { xs: 1.25, sm: 1.5, lg: 1.75 },
            borderBottom: index === rows.length - 1 ? "none" : "1px solid #E6E6E6",
          }}
        >
          <Typography
            sx={{
              fontFamily: FONT,
              fontWeight: 700,
              fontSize: { xs: 12, sm: 12, lg: 13 },
              lineHeight: { xs: 1.35, sm: 1.25, lg: 1 },
              letterSpacing: 0,
              color: "#383838",
            }}
          >
            {row.label}
          </Typography>
          <Typography
            sx={{
              gridColumn: { xs: "1 / -1", sm: "2" },
              gridRow: { xs: 2, sm: "auto" },
              fontFamily: FONT,
              fontWeight: 400,
              fontSize: { xs: 12, sm: 12, lg: 13 },
              lineHeight: { xs: 1.35, sm: 1.25, lg: 1 },
              letterSpacing: 0,
              color: "#383838",
            }}
          >
            {row.value}
          </Typography>
          <Box sx={{ gridColumn: { xs: "2", sm: "3" }, gridRow: { xs: 1, sm: "auto" }, justifySelf: "end" }}>
            <StatusPill status={row.status} />
          </Box>
        </Box>
      ))}

      {gapCount > 0 ? (
        <Box sx={{ px: { xs: 1.5, sm: 2, lg: 0 }, pb: { xs: 1.5, sm: 2, lg: 0 } }}>
          <Box
            sx={{
              boxSizing: "border-box",
              width: "100%",
              height: "auto",
              minHeight: { lg: 108 },
              display: "flex",
              flexDirection: "column",
              gap: { xs: "6px", sm: "6px", lg: "8px" },
              p: { xs: "12px", sm: "14px", lg: "16px" },
              borderRadius: { xs: "6px", lg: "8px" },
              border: "1px solid #FFE999",
              bgcolor: "#FFF8E0",
            }}
          >
            <Typography sx={GAP_TEXT}>
              <Box component="span" sx={{ fontWeight: 700 }}>
                Gaps present, no hard blocker.{" "}
              </Box>
              Conditional go is available if each gap below gets its own logged exception:
            </Typography>
            {softGaps.map((label) => (
              <Typography key={label} sx={{ ...GAP_TEXT, pl: "18px", position: "relative" }}>
                <Box component="span" aria-hidden sx={{ position: "absolute", left: 0 }}>
                  •
                </Box>
                {label}
              </Typography>
            ))}
            <Typography sx={{ ...GAP_TEXT, fontStyle: "italic" }}>
              Each gap needs its own condition, control, and expiry - never one combined entry.
            </Typography>
          </Box>
        </Box>
      ) : null}
    </Box>
  );
}
